import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import {
  getAppointments,
  getAppointment,
  getAvailabilityBlocks,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  cancelAppointment,
  getPatients,
  updateAppointmentAttendance,
  getAppointmentPayments,
  createAppointmentPayment,
  deleteAppointmentPayment,
  getPaymentTypes,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, User, Trash2, DollarSign, AlertTriangle, XCircle, ChevronDown } from 'lucide-react';

moment.locale('es');
const localizer = momentLocalizer(moment);

const statusColors = {
  PENDIENTE: '#E09F3E',
  CONFIRMADA: '#588157',
  CANCELADA: '#9E2A2B',
  NO_SHOW: '#8D99AE',
  COMPLETADA: '#335C67',
  REQUIERE_REAGENDAR: '#9E2A2B',
};

const statusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No se presentó',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Requiere reagendar',
};

const messages = {
  today: 'Hoy',
  previous: 'Anterior',
  next: 'Siguiente',
  month: 'Mes',
  week: 'Semana',
  day: 'Día',
  agenda: 'Agenda',
  date: 'Fecha',
  time: 'Hora',
  event: 'Cita',
  noEventsInRange: 'No hay citas en este rango',
  showMore: (total) => `+ ${total} más`,
};

// Horarios cerrados: solo horas en punto; la hora final se calcula automáticamente (+1 h)
const CLOSED_HOURS = Array.from({ length: 8 }, (_, i) => `${12 + i}:00`);
const addOneHour = (startHhMm) => {
  const [h, m] = startHhMm.split(':').map(Number);
  return `${String(h + 1).padStart(2, '0')}:${String(m || 0).padStart(2, '0')}`;
};
// Para "Forzar cita": cualquier hora del día (00:00–23:00); 23:00 termina a 23:59
const ALL_HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
const endTimeForStart = (startHhMm, isForced) => {
  if (isForced && startHhMm === '23:00') return '23:59';
  return addOneHour(startHhMm);
};

// Convierte "HH:mm" o "HH:mm:ss" a minutos desde medianoche
const timeToMinutes = (t) => {
  const parts = (t || '00:00').toString().split(':');
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  return h * 60 + m;
};

// Dado una fecha YYYY-MM-DD, devuelve rangos ocupados [startMin, endMin] ese día (por citas y bloqueos)
const getOccupiedRangesForDate = (dateStr, appointments, blocks, excludeAppointmentId) => {
  if (!dateStr) return [];
  const ranges = [];
  appointments.forEach((apt) => {
    if (apt.date !== dateStr || (excludeAppointmentId && apt.id === excludeAppointmentId)) return;
    const start = timeToMinutes(apt.start_time);
    const end = timeToMinutes(apt.end_time);
    if (end > start) ranges.push([start, end]);
  });
  blocks.forEach((block) => {
    const startDate = block.start_date;
    const endDate = block.end_date;
    if (dateStr < startDate || dateStr > endDate) return;
    if (block.is_full_day) {
      ranges.push([0, 24 * 60]);
      return;
    }
    const dayStart = timeToMinutes(block.start_time);
    const dayEnd = timeToMinutes(block.end_time);
    const rangeStart = dateStr === startDate ? dayStart : 0;
    const rangeEnd = dateStr === endDate ? dayEnd : 24 * 60;
    if (rangeEnd > rangeStart) ranges.push([rangeStart, rangeEnd]);
  });
  return ranges;
};

// Comprueba si el slot [slotStartMin, slotEndMin) choca con algún rango
const slotOverlapsRanges = (slotStartMin, slotEndMin, ranges) =>
  ranges.some(([rStart, rEnd]) => slotStartMin < rEnd && slotEndMin > rStart);

// Horas de inicio disponibles para una fecha (no ocupadas por citas ni bloqueos)
const getAvailableStartHours = (dateStr, isForced, appointments, blocks, excludeAppointmentId) => {
  const baseHours = isForced ? ALL_HOURS : CLOSED_HOURS;
  if (!dateStr) return baseHours;
  const ranges = getOccupiedRangesForDate(dateStr, appointments, blocks, excludeAppointmentId);
  if (ranges.length === 0) return baseHours;
  const slotDuration = 60; // 1 hora por cita
  return baseHours.filter((h) => {
    const slotStart = timeToMinutes(h);
    const endH = h === '23:00' && isForced ? '23:59' : addOneHour(h);
    const slotEnd = timeToMinutes(endH);
    return !slotOverlapsRanges(slotStart, slotEnd, ranges);
  });
};

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [patients, setPatients] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // En móviles, usar vista DAY por defecto; en desktop, WEEK
  const [currentView, setCurrentView] = useState(
    window.innerWidth < 768 ? Views.DAY : Views.WEEK
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [patientPopoverOpen, setPatientPopoverOpen] = useState(false);
  const [forcedMode, setForcedMode] = useState(false);

  // Detectar cambios en el tamaño de la pantalla
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // Cambiar vista automáticamente si cambia el tamaño
      if (mobile && currentView === Views.WEEK) {
        setCurrentView(Views.DAY);
      } else if (!mobile && currentView === Views.DAY) {
        setCurrentView(Views.WEEK);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentView]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', payment_type_id: '', notes: '', paid_at: '' });
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    date: '',
    start_time: '',
    end_time: '',
    reason: '',
    notes: '',
    status: 'PENDIENTE',
    forced: false,
    type: 'presencial',
    google_calendar_id: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [agendaView, setAgendaView] = useState('active');

  const isCancelledView = agendaView === 'cancelled';
  const isReadOnlyAppointment = isCancelledView || selectedAppointment?.status === 'CANCELADA';

  const fetchData = useCallback(async () => {
    try {
      const startOfMonth = moment(currentDate).startOf('month').subtract(7, 'days').format('YYYY-MM-DD');
      const endOfMonth = moment(currentDate).endOf('month').add(7, 'days').format('YYYY-MM-DD');

      const appointmentParams = { start_date: startOfMonth, end_date: endOfMonth };
      if (isCancelledView) {
        appointmentParams.status = 'CANCELADA';
      }
      
      const [aptsRes, blocksRes, patientsRes] = await Promise.all([
        getAppointments(appointmentParams),
        isCancelledView
          ? Promise.resolve({ data: { data: [] } })
          : getAvailabilityBlocks({ start_date: startOfMonth, end_date: endOfMonth }),
        getPatients(),
      ]);
      
      // Laravel devuelve {data: [...]}, Axios extrae el data, entonces accedemos a .data.data
      setAppointments(Array.isArray(aptsRes.data?.data) ? aptsRes.data.data : (Array.isArray(aptsRes.data) ? aptsRes.data : []));
      setBlocks(Array.isArray(blocksRes.data?.data) ? blocksRes.data.data : (Array.isArray(blocksRes.data) ? blocksRes.data : []));
      setPatients(Array.isArray(patientsRes.data?.data) ? patientsRes.data.data : (Array.isArray(patientsRes.data) ? patientsRes.data : []));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar datos'));
    }
  }, [currentDate, isCancelledView]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const loadPaymentTypes = async () => {
      try {
        const response = await getPaymentTypes();
        setPaymentTypes(response.data?.data || []);
        if (!paymentForm.payment_type_id && response.data?.data?.length) {
          setPaymentForm((prev) => ({ ...prev, payment_type_id: String(response.data.data[0].id) }));
        }
      } catch (error) {
        // silencioso; el formulario mostrará select vacío si falla
      }
    };
    loadPaymentTypes();
  }, []);

  const events = appointments.map((apt) => ({
    id: apt.id,
    title: (apt.patient?.full_name || 'Sin paciente') + (apt.type === 'virtual' ? ' (V)' : '') + (apt.forced ? ' (Forzada)' : ''),
    start: new Date(`${apt.date}T${apt.start_time}`),
    end: new Date(`${apt.date}T${apt.end_time}`),
    status: apt.status,
    resource: apt,
  }));

  const blockEvents = blocks.map((block) => ({
    id: `block-${block.id}`,
    title: 'BLOQUEADO',
    start: block.is_full_day
      ? new Date(`${block.start_date}T00:00:00`)
      : new Date(`${block.start_date}T${block.start_time}`),
    end: block.is_full_day
      ? new Date(`${block.end_date}T23:59:59`)
      : new Date(`${block.end_date}T${block.end_time}`),
    isBlock: true,
  }));

  const allEvents = isCancelledView ? events : [...events, ...blockEvents];

  const isForcedForm = formData.forced || selectedAppointment?.forced;
  const availableStartHours = useMemo(
    () =>
      getAvailableStartHours(
        formData.date,
        isForcedForm,
        appointments,
        blocks,
        selectedAppointment?.id
      ),
    [formData.date, isForcedForm, appointments, blocks, selectedAppointment?.id]
  );

  // Si la hora seleccionada ya no está disponible (p. ej. al cambiar de día), usar la primera disponible
  useEffect(() => {
    if (!formData.date || !formData.start_time) return;
    if (availableStartHours.length === 0) return;
    if (availableStartHours.includes(formData.start_time)) return;
    const isForced = formData.forced || selectedAppointment?.forced;
    const first = availableStartHours[0];
    setFormData((prev) => ({
      ...prev,
      start_time: first,
      end_time: isForced ? endTimeForStart(first, true) : addOneHour(first),
    }));
  }, [formData.date, availableStartHours, formData.start_time, formData.forced, selectedAppointment?.forced]);

  const eventStyleGetter = (event) => {
    if (event.isBlock) {
      return {
        className: 'rbc-event-block',
        style: {
          backgroundColor: '#1e3a5f',
          color: '#f1f5f9',
          border: '1px solid #0f172a',
          borderRadius: '4px',
          fontWeight: 600,
          letterSpacing: '0.02em',
        },
      };
    }
    const status = event.status || event.resource?.status;
    const isForced = event.resource?.forced;

    const classNames = [];
    if (status) {
      classNames.push(status.toLowerCase().replace(/_/g, '-'));
    }

    return {
      className: classNames.join(' '),
      style: {
        borderRadius: '4px',
        border: isForced ? '2px solid #D97706' : 'none',
        boxShadow: isForced ? '0 0 0 1px #F59E0B' : undefined,
      },
    };
  };

  const handleSelectEvent = (event) => {
    if (event.isBlock) return;
    setSelectedAppointment(event.resource);
    const start = event.resource.start_time || '12:00';
    const startNorm = start.length === 5 ? start : start.slice(0, 5);
    const startClosed = CLOSED_HOURS.includes(startNorm) ? startNorm : moment(`1970-01-01 ${startNorm}`).startOf('hour').format('HH:mm');
    const endClosed = addOneHour(startClosed);
    setFormData({
      patient_id: event.resource.patient_id?.toString() || '',
      date: event.resource.date,
      start_time: startClosed,
      end_time: endClosed,
      reason: event.resource.reason || '',
      notes: event.resource.notes || '',
      status: event.resource.status,
      forced: event.resource.forced ?? false,
      type: event.resource.type || 'presencial',
      google_calendar_id: event.resource.google_calendar_id || '',
    });
    setDrawerOpen(true);
  };

  const handleSelectSlot = ({ start }) => {
    if (isCancelledView) return;
    if (moment(start).isBefore(moment(), 'minute')) {
      toast.error('No se pueden crear citas en el pasado');
      return;
    }
    const slotStart = moment(start).startOf('hour');
    const slotEnd = slotStart.clone().add(1, 'hour');
    const overlapsBlock = blocks.some((block) => {
      const blockStart = block.is_full_day
        ? moment(`${block.start_date}T00:00:00`)
        : moment(`${block.start_date}T${block.start_time}`);
      const blockEnd = block.is_full_day
        ? moment(`${block.end_date}T23:59:59`)
        : moment(`${block.end_date}T${block.end_time}`);
      return slotStart.isBefore(blockEnd) && slotEnd.isAfter(blockStart);
    });
    if (overlapsBlock) {
      toast.error('Este horario está bloqueado. No se pueden agendar citas.');
      return;
    }
    if (!forcedMode) {
      const dayOfWeek = moment(start).isoWeekday();
      if (dayOfWeek === 7) {
        toast.error('No se pueden agendar citas los domingos. Use "Forzar cita" para excepción.');
        return;
      }
      const hour = moment(start).hour();
      const minute = moment(start).minute();
      const slotMin = hour * 60 + minute;
      const allowedStart = 12 * 60;
      const allowedEnd = 20 * 60;
      if (slotMin < allowedStart || slotMin >= allowedEnd) {
        toast.error('Horario permitido: L-S 12:00–20:00. Use "Forzar cita" para otro horario.');
        return;
      }
    }
    setSelectedAppointment(null);
    const startSnap = moment(start).startOf('hour');
    const dateStr = startSnap.format('YYYY-MM-DD');
    const startStr = startSnap.format('HH:mm');
    const endStr = startSnap.clone().add(1, 'hour').format('HH:mm');
    setFormData({
      patient_id: '',
      date: dateStr,
      start_time: startStr,
      end_time: endStr,
      reason: '',
      notes: '',
      status: 'PENDIENTE',
      forced: forcedMode,
      type: 'presencial',
      google_calendar_id: '',
    });
    setDrawerOpen(true);
  };

  const handleNewAppointment = () => {
    if (isCancelledView) return;
    const now = moment();
    let start = now.clone().startOf('hour').add(1, 'hour');
    if (start.isBefore(now)) start = now.clone().startOf('hour').add(2, 'hour');
    if (!forcedMode) {
      if (start.isoWeekday() === 7) start = start.clone().add(1, 'day');
      if (start.hour() < 12) start = start.clone().hour(12).minute(0);
      if (start.hour() >= 20) start = start.clone().add(1, 'day').hour(12).minute(0);
    }
    handleSelectSlot({ start: start.toDate() });
  };

  const handleForzarCita = () => {
    if (isCancelledView) return;
    setForcedMode(true);
    setSelectedAppointment(null);
    const now = moment();
    let start = now.clone().startOf('hour').add(1, 'hour');
    if (start.isBefore(now)) start = now.clone().startOf('hour').add(2, 'hour');
    setFormData({
      patient_id: '',
      date: start.format('YYYY-MM-DD'),
      start_time: start.format('HH:mm'),
      end_time: start.clone().add(1, 'hour').format('HH:mm'),
      reason: '',
      notes: '',
      status: 'PENDIENTE',
      forced: true,
      type: 'presencial',
      google_calendar_id: '',
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.patient_id) {
      toast.error('Selecciona un paciente');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        patient_id: parseInt(formData.patient_id),
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        reason: formData.reason,
        notes: formData.notes,
        forced: formData.forced,
        type: formData.type || 'presencial',
        google_calendar_id: formData.google_calendar_id?.trim() || null,
      };

      if (selectedAppointment) {
        await updateAppointment(selectedAppointment.id, { ...payload, status: formData.status });
        toast.success('Cita actualizada');
      } else {
        await createAppointment(payload);
        toast.success('Cita creada');
      }
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedAppointment) return;
    if (!window.confirm('¿Eliminar esta cita? (Se ocultará del listado pero se conserva en el sistema)')) return;
    try {
      await deleteAppointment(selectedAppointment.id);
      toast.success('Cita eliminada');
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al eliminar'));
    }
  };

  const handleCancelClick = () => {
    setCancelReason('');
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    const reason = cancelReason?.trim();
    if (!reason) {
      toast.error('Indica el motivo de cancelación');
      return;
    }
    setCancelLoading(true);
    try {
      await cancelAppointment(selectedAppointment.id, reason);
      toast.success('Cita cancelada');
      setCancelModalOpen(false);
      setDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cancelar'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleAttendanceChange = async (status) => {
    if (!selectedAppointment) return;
    try {
      await updateAppointmentAttendance(selectedAppointment.id, status);
      toast.success('Asistencia actualizada');
      fetchData();
      setSelectedAppointment((prev) => (prev ? { ...prev, attendance_status: status } : null));
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al actualizar'));
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!selectedAppointment) return;
    if (!window.confirm('¿Eliminar este pago?')) return;
    try {
      await deleteAppointmentPayment(selectedAppointment.id, payment.id);
      toast.success('Pago eliminado');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al eliminar pago'));
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!selectedAppointment) return;
    const amount = parseFloat(paymentForm.amount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Monto inválido');
      return;
    }
    if (!paymentForm.payment_type_id) {
      toast.error('Selecciona un tipo de pago');
      return;
    }
    try {
      await createAppointmentPayment(selectedAppointment.id, {
        amount,
        payment_type_id: parseInt(paymentForm.payment_type_id, 10),
        notes: paymentForm.notes || null,
        paid_at: paymentForm.paid_at || new Date().toISOString().slice(0, 10),
      });
      toast.success('Pago registrado');
      setPaymentModalOpen(false);
      setPaymentForm({ amount: '', payment_type_id: paymentForm.payment_type_id, notes: '', paid_at: '' });
      fetchData();
      const res = await getAppointment(selectedAppointment.id);
      const appointmentWithPayments = res.data?.data ?? res.data;
      if (appointmentWithPayments) setSelectedAppointment(appointmentWithPayments);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al registrar pago'));
    }
  };

  const CustomToolbar = ({ label, onNavigate, onView, view, isMobile: mobile }) => {
    return (
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3 md:gap-4 mb-4 md:mb-6">
        {/* Navegación y título - Móvil optimizado */}
        <div className="flex items-center justify-between w-full md:w-auto">
          <div className="flex items-center gap-1 md:gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNavigate('PREV')}
              className="hover:bg-[#F4F1EA] h-10 w-10 md:h-9 md:w-9"
              data-testid="calendar-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              onClick={() => onNavigate('TODAY')}
              className="hover:bg-[#F4F1EA] h-10 px-3 md:h-9 md:px-4 text-sm md:text-base"
              data-testid="calendar-today-btn"
            >
              Hoy
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNavigate('NEXT')}
              className="hover:bg-[#F4F1EA] h-10 w-10 md:h-9 md:w-9"
              data-testid="calendar-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          <h2 className="text-base md:text-xl font-semibold text-[#2B2D42] capitalize px-2">
            {mobile ? moment(currentDate).format('DD MMM') : label}
          </h2>
        </div>
        
        {/* Vistas y botón nueva cita */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <Select
            value={agendaView}
            onValueChange={(value) => {
              setAgendaView(value);
              setDrawerOpen(false);
              setForcedMode(false);
            }}
          >
            <SelectTrigger className="w-full sm:w-[180px] h-10" data-testid="agenda-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Agenda activa</SelectItem>
              <SelectItem value="cancelled">Citas canceladas</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1 flex-1 md:flex-initial">
            {[Views.DAY, Views.WEEK, Views.MONTH].map((v) => (
              <Button
                key={v}
                variant={view === v ? 'default' : 'ghost'}
                size={mobile ? 'sm' : 'default'}
                className={`${
                  view === v 
                    ? 'bg-[#335C67] text-white' 
                    : 'hover:bg-[#F4F1EA]'
                } text-xs md:text-sm flex-1 md:flex-initial`}
                onClick={() => onView(v)}
                data-testid={`calendar-view-${v.toLowerCase()}`}
              >
                {v === Views.MONTH ? 'Mes' : v === Views.WEEK ? 'Sem' : 'Día'}
              </Button>
            ))}
          </div>
          {forcedMode && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Modo: Cita forzada
            </Badge>
          )}
          {!isCancelledView && (
            <>
              <Button
                className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full h-10 px-4 md:px-6 text-sm md:text-base"
                onClick={handleNewAppointment}
                data-testid="calendar-new-appointment-btn"
              >
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                {mobile ? 'Nueva' : 'Nueva Cita'}
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="border-amber-400 text-amber-800 hover:bg-amber-50 rounded-full h-10 px-4 md:px-6 text-sm md:text-base"
                  onClick={handleForzarCita}
                  data-testid="calendar-forzar-cita-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-1 md:mr-2" />
                  Forzar cita
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-3 md:p-6 lg:p-10">
      <div className="mb-4 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#2B2D42] font-['Manrope']">
          {isCancelledView ? 'Citas canceladas' : 'Agenda'}
        </h1>
        <p className="text-sm md:text-base text-[#5D5C61] mt-1">
          {isCancelledView
            ? 'Consulta las citas canceladas en el calendario'
            : 'Gestiona las citas de tus pacientes'}
        </p>
      </div>

      <div className="bg-white rounded-xl md:rounded-2xl border border-slate-100 shadow-sm p-2 md:p-4 lg:p-6 overflow-hidden" data-testid="calendar-container">
        <Calendar
          localizer={localizer}
          events={allEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ 
            height: isMobile 
              ? 'calc(100vh - 240px)' 
              : 'calc(100vh - 280px)', 
            minHeight: isMobile ? '500px' : '600px' 
          }}
          view={currentView}
          onView={setCurrentView}
          date={currentDate}
          onNavigate={setCurrentDate}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          selectable={!isCancelledView}
          eventPropGetter={eventStyleGetter}
          messages={messages}
          components={{
            toolbar: (props) => <CustomToolbar {...props} isMobile={isMobile} forcedMode={forcedMode} />,
          }}
          min={forcedMode ? new Date(0, 0, 0, 0, 0, 0) : new Date(0, 0, 0, 10, 0, 0)}
          max={forcedMode ? new Date(0, 0, 0, 23, 59, 59) : new Date(0, 0, 0, 22, 0, 0)}
          step={isMobile ? 30 : 15}
          timeslots={isMobile ? 2 : 4}
          popup={isMobile}
          dayPropGetter={(date) => {
            if (forcedMode) return {};
            const iso = moment(date).isoWeekday();
            if (iso === 7) {
              return { className: 'rbc-day-sunday-disabled', style: { backgroundColor: '#f1f5f9', pointerEvents: 'none', opacity: 0.6 } };
            }
            return {};
          }}
        />
      </div>

      {/* Appointment Drawer */}
      <Sheet open={drawerOpen} onOpenChange={(open) => { setDrawerOpen(open); if (!open) setForcedMode(false); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto p-4 md:p-6" data-testid="appointment-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope'] flex items-center gap-2">
              {isReadOnlyAppointment
                ? 'Cita cancelada'
                : selectedAppointment
                  ? 'Editar Cita'
                  : 'Nueva Cita'}
              {(formData.forced || selectedAppointment?.forced) && (
                <Badge variant="secondary" className="bg-amber-100 text-amber-800">Forzada</Badge>
              )}
              {isReadOnlyAppointment && (
                <Badge variant="secondary" className="bg-[#9E2A2B]/10 text-[#9E2A2B]">Cancelada</Badge>
              )}
            </SheetTitle>
            <SheetDescription>
              {isReadOnlyAppointment
                ? 'Consulta los datos de la cita cancelada'
                : selectedAppointment
                  ? 'Modifica los datos de la cita'
                  : 'Programa una nueva cita. Horario permitido: L-S 12:00–20:00.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4 text-[#8D99AE]" />
                Paciente
              </Label>
              <Popover open={patientPopoverOpen} onOpenChange={setPatientPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={undefined}
                    className="w-full justify-between h-9 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm font-normal"
                    data-testid="appointment-patient-select"
                    disabled={isReadOnlyAppointment}
                  >
                    <span className="truncate">
                      {(() => {
                        const p = patients.find((x) => x.id.toString() === formData.patient_id);
                        return p ? (p.full_name || `${p.first_name || ''} ${p.paternal_surname || ''}`.trim() || 'Paciente') : 'Seleccionar paciente';
                      })()}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar paciente..." className="h-9" />
                    <CommandList>
                      <CommandEmpty>No hay resultados.</CommandEmpty>
                      <CommandGroup>
                        {patients.map((p) => {
                          const name = p.full_name || `${p.first_name || ''} ${p.paternal_surname || ''}`.trim() || 'Sin nombre';
                          return (
                            <CommandItem
                              key={p.id}
                              value={name}
                              onSelect={() => {
                                setFormData({ ...formData, patient_id: p.id.toString() });
                                setPatientPopoverOpen(false);
                              }}
                            >
                              {name}
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-[#8D99AE]" />
                  Fecha
                </Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="appointment-date-input"
                  required
                  disabled={isReadOnlyAppointment}
                />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                  disabled={!selectedAppointment || isReadOnlyAppointment}
                >
                  <SelectTrigger data-testid="appointment-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tipo de cita</Label>
              <Select
                value={formData.type || 'presencial'}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
                data-testid="appointment-type-select"
                disabled={isReadOnlyAppointment}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="presencial">Presencial</SelectItem>
                  <SelectItem value="virtual">Virtual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* <div className="space-y-2">
              <Label>ID Calendario Google (opcional)</Label>
              <Input
                value={formData.google_calendar_id || ''}
                onChange={(e) => setFormData({ ...formData, google_calendar_id: e.target.value })}
                placeholder="Ej: abc123@group.calendar.google.com"
                data-testid="appointment-google-calendar-id-input"
              />
            </div> */}

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#8D99AE]" />
                Hora
                {/* {(formData.forced || selectedAppointment?.forced)
                  ? 'Hora (cualquier horario; la hora final se asigna en automático)'
                  : 'Hora (consultas de 1 hora; la hora final se asigna en automático)'} */}
              </Label>
              <Select
                value={
                  formData.start_time && availableStartHours.includes(formData.start_time)
                    ? formData.start_time
                    : availableStartHours[0] || ''
                }
                onValueChange={(value) => {
                  const isForced = formData.forced || selectedAppointment?.forced;
                  setFormData({
                    ...formData,
                    start_time: value,
                    end_time: isForced ? endTimeForStart(value, true) : addOneHour(value),
                  });
                }}
                data-testid="appointment-start-time-input"
                disabled={isReadOnlyAppointment || availableStartHours.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={availableStartHours.length === 0 ? 'Sin horarios disponibles' : 'Selecciona hora'} />
                </SelectTrigger>
                <SelectContent>
                  {availableStartHours.map((h) => {
                    const isForced = formData.forced || selectedAppointment?.forced;
                    const end = isForced ? endTimeForStart(h, true) : addOneHour(h);
                    return (
                      <SelectItem key={h} value={h}>
                        {h} – {end}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#8D99AE]">
                {formData.start_time && formData.end_time ? `${formData.start_time} a ${formData.end_time} (1 hora)` : 'Selecciona la hora de inicio'}
              </p>
            </div>

            {(formData.forced || selectedAppointment?.forced) && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">Esta cita se guardará como forzada (fuera de horario normal L-S 12:00–20:00).</p>
              </div>
            )}

            {selectedAppointment?.status === 'CANCELADA' && selectedAppointment?.cancellation_reason && (
              <div className="rounded-lg border border-[#9E2A2B]/20 bg-[#9E2A2B]/5 p-3">
                <p className="text-sm text-[#8D99AE]">Motivo de cancelación</p>
                <p className="text-[#9E2A2B] mt-1">{selectedAppointment.cancellation_reason}</p>
              </div>
            )}

            {selectedAppointment && !isReadOnlyAppointment && (
              <>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">Asistencia</Label>
                  <div className="flex gap-2 flex-wrap">
                    {['pendiente', 'asistio', 'no_asistio'].map((status) => (
                      <Button
                        key={status}
                        type="button"
                        variant={selectedAppointment.attendance_status === status ? 'default' : 'outline'}
                        size="sm"
                        className={selectedAppointment.attendance_status === status ? 'bg-[#335C67]' : ''}
                        onClick={() => handleAttendanceChange(status)}
                      >
                        {status === 'pendiente' ? 'Pendiente' : status === 'asistio' ? 'Asistió' : 'No asistió'}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#8D99AE]" />
                    Pago
                  </Label>
                  <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                    {(() => {
                      const payments = selectedAppointment.payments || [];
                      const hasPayment = payments.length > 0 || selectedAppointment.payment_status === 'pagado';
                      const activePayment = payments[0];
                      return (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {hasPayment ? 'Pagado' : 'Sin pagar'}
                            </span>
                            {hasPayment ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-[#9E2A2B] text-[#9E2A2B] hover:bg-[#9E2A2B]/5"
                                onClick={async () => {
                                  try {
                                    const toDelete = activePayment || (await getAppointmentPayments(selectedAppointment.id).then((r) => r.data?.data?.[0]));
                                    if (!toDelete) {
                                      toast.error('No se encontró un pago activo para esta cita');
                                      return;
                                    }
                                    await deleteAppointmentPayment(selectedAppointment.id, toDelete.id);
                                    toast.success('Pago eliminado');
                                    fetchData();
                                    setSelectedAppointment((prev) =>
                                      prev ? { ...prev, payment_status: 'sin_pago', payments: [] } : null
                                    );
                                  } catch (error) {
                                    toast.error(getApiErrorMessage(error, 'Error al eliminar pago'));
                                  }
                                }}
                              >
                                Eliminar pago
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setPaymentModalOpen(true)}
                                data-testid="appointment-add-payment-btn"
                              >
                                Agregar pago
                              </Button>
                            )}
                          </div>
                          {hasPayment && activePayment && (
                            <div className="text-xs text-[#5D5C61] pt-1 border-t border-slate-100">
                              <span>${Number(activePayment.amount).toFixed(2)}</span>
                              {activePayment.payment_type?.name && (
                                <span> · {activePayment.payment_type.name}</span>
                              )}
                              {activePayment.paid_at && (
                                <span> · {new Date(activePayment.paid_at).toLocaleDateString('es-MX')}</span>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Motivo de consulta</Label>
              <Input
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Consulta de seguimiento..."
                data-testid="appointment-reason-input"
                disabled={isReadOnlyAppointment}
              />
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Notas adicionales..."
                rows={3}
                data-testid="appointment-notes-input"
                disabled={isReadOnlyAppointment}
              />
            </div>

            <div className="flex flex-col gap-3 pt-4">
              <div className="flex gap-3">
                {!isReadOnlyAppointment && (
                  <Button
                    type="submit"
                    className="flex-1 bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                    disabled={isLoading}
                    data-testid="appointment-save-btn"
                  >
                    {isLoading ? 'Guardando...' : 'Guardar'}
                  </Button>
                )}
                {selectedAppointment && (
                  <>
                    {!isReadOnlyAppointment && selectedAppointment.status !== 'CANCELADA' && (
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-full border-[#9E2A2B] text-[#9E2A2B] hover:bg-[#9E2A2B]/10"
                        onClick={handleCancelClick}
                        data-testid="appointment-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Cancelar cita
                      </Button>
                    )}
                    {!isReadOnlyAppointment && (
                      <Button
                        type="button"
                        variant="destructive"
                        className="rounded-full"
                        onClick={handleDelete}
                        data-testid="appointment-delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                    {isReadOnlyAppointment && (
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-full"
                        onClick={() => setDrawerOpen(false)}
                      >
                        Cerrar
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="cancel-appointment-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar cita</DialogTitle>
            <p className="text-sm text-[#5D5C61]">Indica el motivo de la cancelación (obligatorio).</p>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Motivo de cancelación</Label>
              <Textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Ej: El paciente solicitó cambiar de fecha..."
                rows={3}
                data-testid="cancel-reason-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCancelModalOpen(false)}>
                No cancelar
              </Button>
              <Button
                type="button"
                className="bg-[#9E2A2B] hover:bg-[#9E2A2B]/90"
                onClick={handleCancelConfirm}
                disabled={cancelLoading || !cancelReason?.trim()}
                data-testid="cancel-confirm-btn"
              >
                {cancelLoading ? 'Cancelando...' : 'Sí, cancelar cita'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Agregar pago</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Monto</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de pago</Label>
              <Select
                value={paymentForm.payment_type_id}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_type_id: value })}
                disabled={paymentTypes.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder={paymentTypes.length === 0 ? 'Sin tipos de pago' : 'Selecciona tipo'} />
                </SelectTrigger>
                <SelectContent>
                  {paymentTypes.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha de pago</Label>
              <Input
                type="date"
                value={paymentForm.paid_at || new Date().toISOString().slice(0, 10)}
                onChange={(e) => setPaymentForm({ ...paymentForm, paid_at: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Notas..."
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#335C67] hover:bg-[#244048]">
                Guardar pago
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
